/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
* Goal: Summary of receivables
*/
import { TenantService } from '@src/app/shared/tenant.service';
import { DashboardChart } from '@src/app/shared/dashboard/dashboard-widget/dashboard-chart';
import * as Highcharts from 'highcharts';
import { uuid } from '@src/app/model/abstract-object';
import { BankPageNavRoute } from '@src/app/routes/bank-page-nav';


class CashflowChartData {
    txnPeriodId: uuid;
    bankAccountId: uuid;
    name: string;
    from: string;
    to: string;
    moneyIn: string; // Laravel sends number as a string
    moneyOut: string; // Ditto
}

export class CashflowChart extends DashboardChart {
    dataUrl = TenantService.ajaxPath + 'omc/dashboard/cashflow';
    //dataParams = new HttpParams().set('week', 0);

    data: CashflowChartData[];

    title = $localize `Cashflow: Money In vs Money Out`;
    subTitle = $localize `Nett cashflow amount shown as line series`;

    options = [];

    resetChart() {
        // Reset any counters for new data
    }

    getSubTitle() {
        return super.getSubTitle(this.subTitle);
    }

    protected setupData(data: CashflowChartData[]) {
        this.data = data;
    }

    protected createChartOptions(): Highcharts.Options {
        const sortedData = [... this.data].sort(
            (a, b) => new Date(a.from).getTime() - new Date(b.from).getTime()
        );
        const categories = sortedData.map(d => d.name);
        const moneyIn = sortedData.map(d => parseFloat(d.moneyIn));
        const moneyOut = sortedData.map(d => parseFloat(d.moneyOut));
        const netFlow = sortedData.map(d => parseFloat(d.moneyIn) - parseFloat(d.moneyOut));

        return {
            chart: {
                backgroundColor: this.chartBackgroundColor,
                borderRadius: this.chartBorderRadius,
                plotBorderWidth: null,
                type: 'column',
                height: this.height,
                width: this.width,            
            },
            title: this.getChartTitle(),
            subtitle: this.getSubTitle(),
            credits: { enabled: false },
                tooltip: {
                    shared: true,
                    headerFormat: '<span style="font-size: 14px; font-weight: bold">{point.key}</span><br/>',
                    pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>€{point.y:,.2f}</b><br/>'
                }, 
                xAxis: {
                    categories: categories,
                    crosshair: true,
                    title: {
                        text: 'Period'
                    },                  
                },
                yAxis: [{
                        // Primary Y-axis for Money In/Out (Columns)
                        labels: {
                            format: '€{value:,.0f}',
                            style: {
                                color: '#4b5563'
                            }
                        },
                        title: {
                            text: 'Euros in/out',
                        }
                    },
                    {
                        // Secondary Y-axis for Net Flow (Line)
                        title: {
                            text: 'Net Flow',
                            style: {
                                color: this.blueColor,
                            }
                        },
                        labels: {
                            format: '€{value:,.0f}',
                            style: {
                                color: this.blueColor,
                            }
                        },
                        opposite: true, // Display on the right side
                        plotLines: [{ // Add a zero line
                            value: 0,
                            color: this.redColor, // Red line for zero
                            width: 1,
                            zIndex: 4,
                            dashStyle: 'ShortDot'
                        }]
                    }
                ],
            legend: { enabled: false },          
            plotOptions: {
                column: { pointPadding: 0.2, borderWidth: 0, borderRadius: 4 },
                series: {
                    animation: {
                        duration: 1000
                    }
                }
            },
            series: [{
                    name: 'Money In',
                    type: 'column',
                    data: moneyIn,
                    color: this.greenColor,
                    point: {
                        events: {
                            click: this.gotoRoute.bind(this)
                        }
                    }
                },
                {
                    name: 'Money Out',
                    type: 'column',
                    data: moneyOut,
                    color: this.redColor,
                    point: {
                        events: {
                            click: this.gotoRoute.bind(this)
                        }
                    }                    
                },
                {
                    name: 'Net Flow',
                    type: 'line',
                    data: netFlow,
                    yAxis: 1, // Use the secondary Y-axis
                    color: this.blueColor,
                    point: {
                        events: {
                            click: this.gotoRoute.bind(this)
                        }
                    },                    
                    marker: {
                        symbol: 'circle',
                        radius: 5,
                        lineWidth: 2,
                        lineColor: this.blueColor,
                        fillColor: 'white'
                    },
                    dashStyle: 'Solid',
                    tooltip: {
                        valueSuffix: ' (Net)'
                    }
                }
            ],
        } as unknown as Highcharts.Options;
    }
    
    gotoRoute($event: Highcharts.PointClickEventObject) {
        const point = $event.point;
        const period = this.data.find( o => o.name === point.category);
        const link = BankPageNavRoute.getListRouterLink({bankAccountId: period.bankAccountId, txnPeriodId: period.txnPeriodId});
        this.router.navigate(link);
    }
}
